import React, { useState, useImperativeHandle, forwardRef } from "react";

const JobInfoSection = forwardRef(({ job, productCost, onCompleteJob, onRevertJob, onUpdateJob, onLiveCostUpdate, accountSettings }, ref) => {
  // Check if tax option is enabled in account settings (default to true if not set)
  const enableTaxOption = accountSettings?.enableTaxOption !== false;
  const taxRate = accountSettings?.taxRate ? parseFloat(accountSettings.taxRate) / 100 : 0.06;

  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notes, setNotes] = useState(job.description || "");
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  const [includeTax, setIncludeTax] = useState(job.includeTax || false);
  const [isSavingTax, setIsSavingTax] = useState(false);

  // Fulfillment tracking state
  const [fulfillmentStatus, setFulfillmentStatus] = useState(job.fulfillmentStatus || "PENDING");
  const [trackingNumber, setTrackingNumber] = useState(job.trackingNumber || "");
  const [shippingCarrier, setShippingCarrier] = useState(job.shippingCarrier || "");
  const [isEditingFulfillment, setIsEditingFulfillment] = useState(false);
  const [isSavingFulfillment, setIsSavingFulfillment] = useState(false);

  const [refreshKey, setRefreshKey] = useState(0);

  const handleSaveNotes = async () => {
    setIsSavingNotes(true);
    try {
      await onUpdateJob({ id: job.id, description: notes });
      job.description = notes;
      setIsEditingNotes(false);
    } catch (error) {
      console.error("Failed to update notes:", error);
      alert("Failed to save notes: " + (error.response?.data || error.message));
    } finally {
      setIsSavingNotes(false);
    }
  };

  const handleCancelNotes = () => {
    setNotes(job.description || "");
    setIsEditingNotes(false);
  };

  const handleToggleTax = async (checked) => {
    setIsSavingTax(true);
    if (onLiveCostUpdate) {
      onLiveCostUpdate({ includeTax: checked });
    }
    try {
      await onUpdateJob({ id: job.id, includeTax: checked });
      job.includeTax = checked;
      setIncludeTax(checked);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error("Failed to update tax setting:", error);
      alert("Failed to update tax setting: " + (error.response?.data || error.message));
      if (onLiveCostUpdate) {
        onLiveCostUpdate({ includeTax: !checked });
      }
    } finally {
      setIsSavingTax(false);
    }
  };

  const handleSaveFulfillment = async () => {
    setIsSavingFulfillment(true);
    try {
      await onUpdateJob({
        id: job.id,
        fulfillmentStatus,
        trackingNumber,
        shippingCarrier
      });
      job.fulfillmentStatus = fulfillmentStatus;
      job.trackingNumber = trackingNumber;
      job.shippingCarrier = shippingCarrier;
      setIsEditingFulfillment(false);
      setRefreshKey(prev => prev + 1);
    } catch (error) {
      console.error("Failed to update fulfillment:", error);
      alert("Failed to save fulfillment info: " + (error.response?.data || error.message));
    } finally {
      setIsSavingFulfillment(false);
    }
  };

  const handleCancelFulfillment = () => {
    setFulfillmentStatus(job.fulfillmentStatus || "PENDING");
    setTrackingNumber(job.trackingNumber || "");
    setShippingCarrier(job.shippingCarrier || "");
    setIsEditingFulfillment(false);
  };

  // Expose save function to parent components via ref
  useImperativeHandle(ref, () => ({
    saveAllPendingChanges: async () => {
      // No pending changes to save since we removed auto-save fields
    }
  }));

  // Financial data - product cost comes from products added to the order
  const currentProductCost = productCost || 0;
  // Only apply tax if enableTaxOption is true AND includeTax is checked
  const effectiveIncludeTax = enableTaxOption && includeTax;
  const taxAmount = effectiveIncludeTax ? currentProductCost * taxRate : 0;
  const totalOrderCost = currentProductCost + taxAmount;
  const taxPercentDisplay = (taxRate * 100).toFixed(0);

  const getFulfillmentStatusStyle = (status) => {
    switch (status) {
      case "DELIVERED":
        return { backgroundColor: '#ECFDF5', color: '#047857', border: '1px solid #A7F3D0' };
      case "SHIPPED":
        return { backgroundColor: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE' };
      case "PROCESSING":
        return { backgroundColor: '#FEF3C7', color: '#92400E', border: '1px solid #FDE68A' };
      default: // PENDING
        return { backgroundColor: '#F3F4F6', color: '#374151', border: '1px solid #D1D5DB' };
    }
  };

  const getFulfillmentLabel = (status) => {
    switch (status) {
      case "DELIVERED": return "Delivered";
      case "SHIPPED": return "Shipped";
      case "PROCESSING": return "Processing";
      default: return "Pending";
    }
  };

  return (
    <div className="space-y-2 sm:space-y-3">
      {/* Customer Information Card */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-lg border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex-1">
            <h4 className="font-medium text-gray-500 text-xs uppercase tracking-wide mb-1">Customer</h4>
            <p className="text-base sm:text-lg font-bold text-gray-900">{job.customerName || job.customer?.name || job.customer?.username || "Unknown Customer"}</p>
            {job.customer?.businessName && (
              <p className="text-sm text-gray-600">{job.customer.businessName}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-gray-600">
            {job.customer?.phone && (
              <span className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded border border-gray-200">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                {job.customer.phone}
              </span>
            )}
            {(job.customer?.city || job.customer?.state) && (
              <span className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded border border-gray-200">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                {[job.customer.city, job.customer.state].filter(Boolean).join(", ")}
              </span>
            )}
            {job.customer?.totalOrders > 0 && (
              <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-2 py-1 rounded border border-blue-200">
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" /></svg>
                {job.customer.totalOrders} orders
              </span>
            )}
          </div>
        </div>
        {job.customer?.addressLine1 && (
          <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-600">
            <span className="font-medium">Address: </span>
            {job.customer.addressLine1}
            {job.customer.addressLine2 && `, ${job.customer.addressLine2}`}
            {job.customer.city && `, ${job.customer.city}`}
            {job.customer.state && `, ${job.customer.state}`}
            {job.customer.zip && ` ${job.customer.zip}`}
          </div>
        )}
      </div>

      {/* Status Row: Order Status, Fulfillment Status, Invoice Date */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div className="bg-gray-50 p-3 rounded border border-gray-200">
          <h4 className="font-medium text-gray-500 text-xs uppercase tracking-wide mb-2">Order Status</h4>
          <div className="flex flex-col gap-2">
            <span
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm font-medium w-fit"
              style={{
                backgroundColor: job.status === "IN_PROGRESS" ? '#EFF6FF' : '#ECFDF5',
                color: job.status === "IN_PROGRESS" ? '#1E40AF' : '#047857',
                border: job.status === "IN_PROGRESS" ? '1px solid #BFDBFE' : '1px solid #A7F3D0',
              }}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: job.status === "IN_PROGRESS" ? '#3B82F6' : '#10B981' }}
              />
              {job.status === "IN_PROGRESS" ? "In Progress" : "Completed"}
            </span>
            {job.status === "IN_PROGRESS" && (
              <button
                onClick={onCompleteJob}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-500 text-white rounded-md text-sm font-medium hover:bg-emerald-600 transition-colors shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                Mark Complete
              </button>
            )}
            {job.status === "COMPLETED" && (
              <button
                onClick={onRevertJob}
                className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-amber-500 text-white rounded-md text-sm font-medium hover:bg-amber-600 transition-colors shadow-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
                Revert to In Progress
              </button>
            )}
          </div>
        </div>
        <div className="bg-gray-50 p-3 rounded border border-gray-200">
          <h4 className="font-medium text-gray-500 text-xs uppercase tracking-wide mb-2">Fulfillment</h4>
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded text-sm font-medium"
            style={getFulfillmentStatusStyle(fulfillmentStatus)}
          >
            {getFulfillmentLabel(fulfillmentStatus)}
          </span>
        </div>
        <div className="bg-gray-50 p-3 rounded border border-gray-200">
          <h4 className="font-medium text-gray-500 text-xs uppercase tracking-wide mb-2">Invoice Date</h4>
          <p className="text-sm font-semibold text-gray-900">{job.startDate || job.orderDate ? new Date(job.startDate || job.orderDate).toLocaleDateString() : new Date().toLocaleDateString()}</p>
        </div>
      </div>

      {/* Fulfillment Tracking Section */}
      <div className="bg-gray-50 p-2 rounded border border-gray-200">
        <div className="flex justify-between items-center mb-2">
          <h4 className="font-medium text-gray-600 text-xs">Fulfillment & Tracking</h4>
          {!isEditingFulfillment && (
            <button
              onClick={() => setIsEditingFulfillment(true)}
              className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors"
            >
              Edit
            </button>
          )}
        </div>

        {isEditingFulfillment ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className="block text-xs text-gray-600 mb-1">Status</label>
                <select
                  value={fulfillmentStatus}
                  onChange={(e) => setFulfillmentStatus(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                >
                  <option value="PENDING">Pending</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="SHIPPED">Shipped</option>
                  <option value="DELIVERED">Delivered</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Carrier</label>
                <select
                  value={shippingCarrier}
                  onChange={(e) => setShippingCarrier(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                >
                  <option value="">Select carrier...</option>
                  <option value="USPS">USPS</option>
                  <option value="UPS">UPS</option>
                  <option value="FedEx">FedEx</option>
                  <option value="DHL">DHL</option>
                  <option value="Local Delivery">Local Delivery</option>
                  <option value="Pickup">Pickup</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-600 mb-1">Tracking #</label>
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  placeholder="Enter tracking number"
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSaveFulfillment}
                disabled={isSavingFulfillment}
                className={`px-3 py-1 rounded text-xs ${
                  isSavingFulfillment ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'
                } text-white transition-colors`}
              >
                {isSavingFulfillment ? "Saving..." : "Save"}
              </button>
              <button
                onClick={handleCancelFulfillment}
                disabled={isSavingFulfillment}
                className="px-3 py-1 bg-gray-400 text-white rounded text-xs hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div>
              <span className="text-gray-500 text-xs">Status:</span>
              <p className="font-medium">{getFulfillmentLabel(fulfillmentStatus)}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs">Carrier:</span>
              <p className="font-medium">{shippingCarrier || "Not set"}</p>
            </div>
            <div>
              <span className="text-gray-500 text-xs">Tracking:</span>
              <p className="font-medium break-all">{trackingNumber || "Not set"}</p>
            </div>
          </div>
        )}
      </div>

      {/* Financial Metrics Row */}
      <div key={refreshKey} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {/* Product Cost (from added products) */}
        <div className="bg-gray-50 p-2 rounded border border-gray-200">
          <h4 className="font-medium text-gray-600 mb-1 text-xs">Product Cost</h4>
          <p className="text-lg font-bold text-gray-900">${currentProductCost.toFixed(2)}</p>
          <p className="text-xs text-gray-500">Sum of products added below</p>
        </div>

        {/* Tax Toggle - Only show if enableTaxOption is true in account settings */}
        {enableTaxOption && (
          <div className="bg-gray-50 p-2 rounded border border-gray-200">
            <h4 className="font-medium text-gray-600 mb-1 text-xs">Tax ({taxPercentDisplay}%)</h4>
            <div className="flex items-center gap-2 mt-2">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={includeTax}
                  onChange={(e) => handleToggleTax(e.target.checked)}
                  disabled={isSavingTax}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
              <span className="text-sm font-semibold">
                {includeTax ? `$${taxAmount.toFixed(2)}` : "Not included"}
              </span>
            </div>
          </div>
        )}

        {/* Total with Breakdown */}
        <div className="bg-blue-50 p-2 rounded border-2 border-blue-500">
          <h4 className="font-semibold text-gray-700 mb-1.5 text-xs">Total Cost</h4>
          <p className="text-2xl font-bold text-gray-900 mb-2">${totalOrderCost.toFixed(2)}</p>
          <div className="space-y-1 text-xs border-t border-blue-200 pt-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Products:</span>
              <span className="font-semibold">${currentProductCost.toFixed(2)}</span>
            </div>
            {effectiveIncludeTax && (
              <div className="flex justify-between">
                <span className="text-gray-600">Tax ({taxPercentDisplay}%):</span>
                <span className="font-semibold">${taxAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between pt-1 border-t border-blue-300">
              <span className="font-semibold text-gray-700">Total:</span>
              <span className="font-bold text-gray-900">${totalOrderCost.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Order Notes Section */}
      <div className="bg-gray-50 p-2 rounded border border-gray-200">
        <div className="flex justify-between items-center mb-2">
          <h4 className="font-medium text-gray-600 text-xs">Order Notes</h4>
          {!isEditingNotes && (
            <button
              onClick={() => setIsEditingNotes(true)}
              className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600 transition-colors"
            >
              {job.description ? "Edit" : "Add"}
            </button>
          )}
        </div>

        {isEditingNotes ? (
          <div className="space-y-2">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add order notes..."
              className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm min-h-[80px]"
              rows="3"
            />
            <div className="flex gap-2">
              <button
                onClick={handleSaveNotes}
                disabled={isSavingNotes}
                className={`px-2 py-1 rounded text-xs ${
                  isSavingNotes
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-500 hover:bg-green-600'
                } text-white transition-colors`}
              >
                {isSavingNotes ? "Saving..." : "Save"}
              </button>
              <button
                onClick={handleCancelNotes}
                disabled={isSavingNotes}
                className="px-2 py-1 bg-gray-400 text-white rounded text-xs hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-800 break-words whitespace-pre-wrap">
            {job.description || "No order notes added yet"}
          </p>
        )}
      </div>
    </div>
  );
});

JobInfoSection.displayName = 'JobInfoSection';

export default JobInfoSection;
